<template>
   <!-- [theme class css in cms-themes.scss] -->
   <div class="flex flex-col" :class="`theme-${containerTheme}`">
      <div
         class="flex flex-col p-6 md:grow md:pl-10 md:max-lg:pr-[14.6rem] md:max-lg:pt-10 lg:px-12 lg:pt-6 xl:px-16 xl:pt-16"
         :class="{ grow: !cta }"
      >
         <Kicker :text="kicker" />

         <h1 class="mt-6 text-3xl text-lighter">{{ heading }}</h1>
         <RichTextRenderer v-if="description" class="mt-3 text-lighter leading-normal" :document="description" />
      </div>

      <div class="flex flex-col md:flex-row" :class="{ 'max-md:grow': cta }">
         <div
            v-if="cta"
            class="flex grow items-start p-6 pt-0 md:items-end md:max-lg:py-10 md:max-lg:pl-10 lg:max-xl:py-12 lg:max-xl:pl-12 xl:py-16 xl:pl-16"
         >
            <PageCTAItems
               v-if="cta"
               data-cy="base-card-button"
               :ctas="[cta]"
               :localeId="localeId"
               :theme="containerTheme"
               :isPageCta="false"
            />
         </div>

         <picture v-if="image" class="flex items-end justify-center" :class="{ 'w-full': !cta }">
            <source :srcset="image.url.sm" media="(max-width: 47.94em)" />
            <img
               :class="[buttonImageClass, cta ? 'object-center md:object-right-bottom' : 'object-center']"
               class="object-contain"
               :src="image.url.lg"
               :alt="image.altTag"
            />
         </picture>
      </div>
   </div>
</template>

<script setup lang="ts">
import { type ImageData, type ContainerTheme, Theme, type RichText } from "@/types";
import type { LinkElementData } from "@/components/ui/LinkElement.vue";
import type { DefaultButtonCTAData } from "@/components/ui/buttons/DefaultButton.vue";

export interface BaseCard {
   type: string;
   kicker: string;
   heading: string;
   description?: RichText;
   image?: ImageData;
   cta?: LinkElementData | DefaultButtonCTAData; // base cards can be links or buttons
   theme?: ContainerTheme;
   // locale ID to use in the card links
   // NOTE: This will be set to null whenever we default to the session locale (e.g., 'about-us' blogpost link),
   // while it will be defined for any study-related link (e.g., link for continuing a flow)
   localeId?: string | null;
}

const props = withDefaults(defineProps<BaseCard>(), {
   theme: Theme.Grey,
   localeId: null,
});

// computed
// -----------------------------------------
const containerTheme = computed(() => (!props.theme ? Theme.Grey : props.theme));

const buttonImageClass = computed(() => {
   if (props.cta && props.image) {
      return "button-and-image";
   } else if (props.cta) {
      return "button-only";
   } else if (props.image) {
      return "image-only";
   }

   return "button-and-image";
});
</script>
<style scoped lang="scss">
.button-and-image {
   @apply w-full h-40 md:w-[226px] md:h-[210px] lg:w-[180px] lg:h-[200px] xl:w-[280px] xl:h-[260px];
}
.image-only {
   @apply w-[328px] h-40 md:w-[448px] md:h-[200px] lg:w-[448px] lg:h-[200px] xl:w-[592px] xl:h-[260px];
}
</style>
